import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import dayjs from "dayjs";
import { FaCalendarAlt, FaChevronDown, FaCheck } from "react-icons/fa";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Button, DatePicker } from "rsuite";
import { GoArrowLeft } from "react-icons/go";
import {
  useLazyPopulateSelectAppointmentModalQuery,
  useRescheduleAppointmentMutation,
} from "../State/Api/MakeAppointmentApiSlice";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import "rsuite/dist/rsuite.min.css";

const visitTypes = [{ id: 2, name: "In-Person" }];

const appointmentTypes = [
  { id: 1, name: "General Check-up" },
  { id: 2, name: "Follow-up" },
  // { id: 3, name: "Emergency" },
];

const RescheduleAppointment = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const { appointmentData = {} } = location.state || {};

  const [timeSlots, setTimeSlots] = useState([]);

  const formatAptmntDateForApi = (date) =>
    date ? dayjs(date).format("YYYY-MM-DD") : "";

  const [fetchSelectAppointmentModalData] =
    useLazyPopulateSelectAppointmentModalQuery();
  const [rescheduleAppointment, { isLoading }] =
    useRescheduleAppointmentMutation();

  const formik = useFormik({
    initialValues: {
      date: null,
      timeSlot: "",
      visitType: visitTypes[0],
      appointmentType: appointmentTypes[0],
      reasonForReschedule: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.timeSlot) {
        errors.timeSlot = "Time slot is required";
      }
      if (!values.reasonForReschedule.trim()) {
        errors.reasonForReschedule = "Reason for reschedule is required";
      }
      if (!values.date) {
        errors.date = "Date is required";
      }
      if (!values.visitType) {
        errors.visitType = "Visit type is required";
      }
      if (!values.appointmentType) {
        errors.appointmentType = "Appointment type is required";
      }
      return errors;
    },
    onSubmit: async (values) => {
      if (!appointmentData?.appointmentID) {
        alert("Appointment ID is missing.");
        return;
      }
      const payload = {
        appointmentID: appointmentData.appointmentID,
        appointmentDate: dayjs(values.date).format("YYYY-MM-DD"),
        timeSlot: values.timeSlot,
        visitType: values.visitType.name,
        doctorID: appointmentData.doctorID,
        doctorName: appointmentData.doctorName,
        department: appointmentData.department,
        appointmentType: values.appointmentType.name,
        ReasonForReschedule: values.reasonForReschedule,
      };
      try {
        const response = await rescheduleAppointment(payload).unwrap();
        if (response?.result_Code === 100) {
          alert("Appointment rescheduled successfully (created)!");
          navigate("/DoctorAppointments");
        } else if (response?.result_Code === 200) {
          alert("Appointment rescheduled successfully (updated)!");
          navigate("/DoctorAppointments");
        } else if (response?.result_Code === 300) {
          alert("Selected time slot already exists. Please choose another slot.");
        } else {
          alert("Unexpected response. Please try again.");
        }
      } catch (err) {
        console.error("Reschedule failed:", err);
        alert("Failed to reschedule.");
      }
    },
  });

  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!formik.values.date || !appointmentData.doctorID) return;

      const weekday = formatAptmntDateForApi(formik.values.date);

      try {
        const result = await fetchSelectAppointmentModalData({
          searchindex: "Timeslot",
          userClinicID: localStorage.getItem("userClinicID"),
          specialization: "",
          doctorID: appointmentData.doctorID,
          patient: "",
          weekday,
        }).unwrap();

        const now = new Date();
        const isToday =
          now.toDateString() === new Date(formik.values.date).toDateString();

        const slots = Array.isArray(result.timeslots)
          ? result.timeslots
              .map((s) => s.timeslot)
              .filter((slot) => {
                if (!isToday) return true;
                const [time, period] = slot
                  .match(/(\d{1,2}:\d{2})(AM|PM)/i)
                  .slice(1);
                let [h, m] = time.split(":").map(Number);
                if (period === "PM" && h !== 12) h += 12;
                if (period === "AM" && h === 12) h = 0;
                const slotMinutes = h * 60 + m;
                const nowMinutes = now.getHours() * 60 + now.getMinutes();
                return slotMinutes > nowMinutes;
              })
          : [];

        setTimeSlots(slots);
      } catch (err) {
        console.error("Error fetching timeslots:", err);
        setTimeSlots([]);
      }
    };
    fetchTimeSlots();
  }, [formik.values.date, appointmentData.doctorID]);

  const styles = `
  p {
    font-size : 0.875em;
  }
  .reschedule-container {
    display: flex;
    justify-content: space-between;
    padding: 2rem;
    gap: 2rem;
    flex-wrap: wrap;
  }
  .left-card, .right-card {
    background: #fff;
    border-radius: 10px;
    padding: 1.5rem;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.05);
  }
  .left-card {
    flex: 2;
    min-width: 60%;
  }
  .right-card {
    flex: 1;
    max-height:450px;
    min-width: 300px;
  }
  .section-title {
    font-weight: 600;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  .section-subtext {
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 1rem;
  }
  .current-appointment {
    display: flex;
    background: #f1f5f9;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .form-grid {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .form-group {
    flex: 1;
    min-width: 200px;
    margin-bottom: 1rem;
  }
  label {
    font-weight: 500;
    display: block;
    margin-bottom: 0.3rem;
  }
  input[type="date"],
  input[type="time"],
  textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 6px;
  }
  textarea {
    resize: vertical;
  }
  .checkbox-group {
    margin-top: 1rem;
  }
  .checkbox-group label {
    display: block;
    margin-bottom: 0.5rem;
  }
  .form-actions {
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }
  .btn-cancel,
  .btn-submit {
    padding: 0.6rem 1.2rem;
    border-radius: 6px;
    border: none;
    font-weight: 500;
  }
  .btn-cancel {
    background: #f3f4f6;
  }
  .btn-submit {
    background: #111;
    color: #fff;
  }
  .details-section {
    border-top: 1px solid #eee;
    padding-top: 1rem;
  }
  .details-row {
    margin-bottom: 1rem;
  }
  .details-row strong {
    display: block;
  }
  .time-range-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    margin: 1.5rem 0;
  }
  .time-input {
    min-width: 150px;
  }
  .dash {
    font-weight: bold;
    font-size: 1.2rem;
  }
  .save-button {
    background-color: #111827;
    color: white;
    padding: 0.5rem 1.1rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
  }
  .rs-input-group.rs-input-group-inside {
    height: 40px !important;
  }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="reschedule-container">
        <div className="row">
          <div
            style={{ borderBottom: "1px dashed gray", paddingBottom: "1rem" }}
            className="col-12 d-flex justify-content-between"
          >
            <div className="d-flex justify-content-between">
              <button
                onClick={handleBack}
                className="bg-[#fff] inline-flex items-center justify-center shrink-0 gap-0 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground size-10"
              >
                <GoArrowLeft />
                <span className="sr-only">Back</span>
              </button>
              <div className="ms-3">
                <h2 className="h3 fw-bold">Reschedule Appointment</h2>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className="left-card">
          <div className="current-appointment">
            <FaCalendarAlt style={{ marginTop: "4px" }} />
            <div>
              <strong>Current Appointment</strong>
              <div>
                {dayjs(appointmentData?.appointmentDate).format("DD-MMM-YYYY")} at{" "}
                {appointmentData?.timeSlot || "-"}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-4">
              <div className="form-group">
                <label>Date</label>
                <DatePicker
                  oneTap
                  style={{ width: 350 }}
                  disabledDate={(date) => date.getTime() < new Date().setHours(0, 0, 0, 0)}
                  format="dd-MMM-yyyy"
                  value={formik.values.date}
                  onChange={(val) => formik.setFieldValue("date", val)}
                  placeholder="Select date"
                />
                {formik.touched.date && formik.errors.date && (
                  <p className="text-red-500 text-sm">{formik.errors.date}</p>
                )}
              </div>
            </div>
            <div className="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-4">
              <div className="form-group">
                <label>Visit Type</label>
                <Listbox
                  value={formik.values.visitType}
                  onChange={(val) => {
                    const selected = visitTypes.find((v) => v.id === val.id) || val;
                    formik.setFieldValue("visitType", selected);
                  }}
                >
                  <div className="relative">
                    <ListboxButton className="w-full h-[40px] border border-gray-300 rounded px-2 text-sm text-muted text-start focus:outline-none focus:ring-2 focus:ring-primary-500">
                      {formik.values.visitType?.name || "Select visit type"}
                      <FaChevronDown className="absolute right-2 top-3.5 text-gray-400" size={14} />
                    </ListboxButton>
                    <ListboxOptions className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black/10 focus:outline-none">
                      {visitTypes.map((type) => (
                        <ListboxOption
                          key={type.id}
                          value={type}
                          className={({ focus, selected }) =>
                            clsx(
                              "relative cursor-default select-none py-2 pl-10 pr-4",
                              focus ? "bg-gray-100" : "",
                              selected ? "font-semibold text-gray-900" : "text-gray-700"
                            )
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className="block truncate">{type.name}</span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                  <FaCheck className="text-green-600" size={14} />
                                </span>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>
            </div>
            <div className="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-4">
              <div className="form-group">
                <label>Appointment Type</label>
                <Listbox
                  value={formik.values.appointmentType}
                  onChange={(val) => {
                    const selected =
                      appointmentTypes.find((v) => v.id === val.id) || val;
                    formik.setFieldValue("appointmentType", selected);
                  }}
                >
                  <div className="relative">
                    <ListboxButton className="w-full h-[40px] border border-gray-300 rounded px-2 text-sm text-muted text-start focus:outline-none focus:ring-2 focus:ring-primary-500">
                      {formik.values.appointmentType?.name || "Select Appointment type"}
                      <FaChevronDown className="absolute right-2 top-3.5 text-gray-400" size={14} />
                    </ListboxButton>
                    <ListboxOptions className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black/10 focus:outline-none">
                      {appointmentTypes.map((type) => (
                        <ListboxOption
                          key={type.id}
                          value={type}
                          className={({ focus, selected }) =>
                            clsx(
                              "relative cursor-default select-none py-2 pl-10 pr-4",
                              focus ? "bg-gray-100" : "",
                              selected ? "font-semibold text-gray-900" : "text-gray-700"
                            )
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className="block truncate">{type.name}</span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                  <FaCheck className="text-green-600" size={14} />
                                </span>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>
            </div>
          </div>

          <div style={{ maxHeight: "500px", overflowY: "auto", padding: "10px" }}>
            <label className="styled-label" htmlFor="inpt-avl-slot">
              Time slots
            </label>

            <div className="bdr-styled-container w-100 border rounded">
              {timeSlots.length > 0 ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: "10px",
                    padding: "10px",
                  }}
                >
                  {timeSlots.map((slot, idx) => (
                    <Button
                      key={idx}
                      appearance="ghost"
                      color={formik.values.timeSlot === slot ? "green" : undefined}
                      onClick={() => formik.setFieldValue("timeSlot", slot)}
                      disabled={formik.values.appointmentType?.name === "Emergency"}
                      style={{
                        opacity:
                          formik.values.appointmentType?.name === "Emergency" ? 0.5 : 1,
                        cursor:
                          formik.values.appointmentType?.name === "Emergency"
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              ) : (
                <p style={{ padding: "10px", color: "#999" }}>
                  No available time slots.
                </p>
              )}
              {formik.values.appointmentType?.name === "Emergency" && (
                <p className="text-red-500 text-sm px-3 pb-2">
                  Time slots are disabled for Emergency appointments.
                </p>
              )}
            </div>
          </div>

          <div className="form-group p-2">
            <label>Reason for Rescheduling</label>
            <textarea
              name="reasonForReschedule"
              value={formik.values.reasonForReschedule}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Please provide a reason for rescheduling this appointment"
              rows={3}
            />
            {formik.touched.reasonForReschedule && formik.errors.reasonForReschedule && (
              <p className="text-red-500 text-sm">{formik.errors.reasonForReschedule}</p>
            )}
          </div>

          <div className="p-2">
            <p className="section-subtext">
              This information will be used for record-keeping purposes.
            </p>
            <div className="checkbox-group">
              <label>
                <input type="checkbox" defaultChecked /> Notify patient about
                the rescheduled appointment
              </label>
              <label>
                <input type="checkbox" defaultChecked /> Notify doctor about the
                rescheduled appointment
              </label>
            </div>
          </div>

          <div className="form-actions">
            <Link to="/DoctorAppointments">
              <button type="button" className="btn-back">
                Cancel
              </button>
            </Link>
            <button className="gradient-dark-button" disabled={isLoading} type="submit">
              {isLoading ? "Rescheduling..." : "Reschedule Appointment"}
            </button>
          </div>
        </form>

        <div className="col-md-4 right-card">
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Appointment Details</h5>
              <div className="d-flex flex-row align-items-center mt-2 gap-3">
                <img
                  src="https://images.unsplash.com/photo-1526779259212-939e64788e3c?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8ZnJlZSUyMGltYWdlc3xlbnwwfHwwfHx8MA%3D%3D"
                  alt="Patient"
                  className="rounded-circle mb-2"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                  }}
                />

                <div className="d-flex align-items-start flex-column">
                  <p className="fw-semibold mb-1">{appointmentData.patientName}</p>
                  <p className="text-muted small">ID: {appointmentData.patientIDUniqueID}</p>
                  <p className="text-muted small">Gender: {appointmentData.gender}</p>
                  <p className="text-muted small">Age: {appointmentData.age}</p>
                </div>
              </div>
              <button
                className="gradient-dark-button w-100 mt-3"
                onClick={() =>
                  navigate(`/patientsOverview/${appointmentData.patientUserUniqueID}?from=${encodeURIComponent(
              location.pathname + location.search
            )}`)
                }
              >
                View Patient Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RescheduleAppointment;
